import {
  useListRevisions,
  useRollbackDashboard,
  getGetDashboardQueryKey,
  getListDashboardsQueryKey,
  type DashboardsRevisionSummary,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  History,
  RotateCcw,
  AlertCircle,
  AlertTriangle,
  Clock,
  User,
  FileText,
} from 'lucide-react';
import { useState, useCallback } from 'react';

import { Button, Modal, useToast } from '@/components/ui';

export interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  dashboardName: string;
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Unknown';
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

export function HistoryModal({ isOpen, onClose, dashboardId, dashboardName }: HistoryModalProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [rollbackTarget, setRollbackTarget] = useState<DashboardsRevisionSummary | null>(null);

  const { data, isLoading, isError, refetch } = useListRevisions(dashboardId, {
    query: {
      enabled: isOpen && !!dashboardId,
    },
  });

  const revisions = data?.revisions ?? [];

  const { mutate: rollbackDashboard, isPending: isRollingBack } = useRollbackDashboard({
    mutation: {
      onSuccess: () => {
        addToast({
          message: `Dashboard rolled back to version ${rollbackTarget?.version}`,
          variant: 'success',
        });
        queryClient.invalidateQueries({ queryKey: getGetDashboardQueryKey(dashboardId) });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
        setRollbackTarget(null);
        onClose();
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to rollback dashboard',
          variant: 'error',
        });
        setRollbackTarget(null);
      },
    },
  });

  const handleRollback = useCallback(() => {
    if (rollbackTarget?.id) {
      rollbackDashboard({
        id: dashboardId,
        data: { revisionId: rollbackTarget.id },
      });
    }
  }, [rollbackTarget, dashboardId, rollbackDashboard]);

  const handleClose = useCallback(() => {
    setRollbackTarget(null);
    onClose();
  }, [onClose]);

  // Rollback confirmation view
  if (rollbackTarget) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Confirm Rollback"
        subtitle={`Roll back "${dashboardName}" to version ${rollbackTarget.version}`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRollbackTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRollback} loading={isRollingBack}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Rollback
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 dark:bg-amber-500/10">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                This action will revert the dashboard
              </p>
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                All changes made after version {rollbackTarget.version} will be lost. A new revision
                will be created to track this rollback.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
              <span className="font-medium">Version {rollbackTarget.version}</span>
              <span className="text-gray-400 dark:text-zinc-600">&bull;</span>
              <span className="text-gray-500 dark:text-zinc-500">
                {formatDate(rollbackTarget.createdAt)}
              </span>
            </div>
            {rollbackTarget.message && (
              <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
                {rollbackTarget.message}
              </p>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Dashboard History"
      subtitle={`Revision history for "${dashboardName}"`}
      size="lg"
      footer={
        <Button variant="ghost" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="rounded-lg bg-red-50 p-4 text-center dark:bg-red-500/10">
            <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
            <p className="text-sm text-red-600 dark:text-red-400">Failed to load revisions</p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-sm text-red-500 hover:text-red-400"
            >
              Try again
            </button>
          </div>
        ) : revisions.length === 0 ? (
          <div className="rounded-lg border border-gray-200 p-8 text-center dark:border-white/[0.08]">
            <History className="mx-auto mb-3 h-8 w-8 text-gray-400 dark:text-zinc-500" />
            <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">No revisions yet</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
              Revisions are created when you save changes to the dashboard
            </p>
          </div>
        ) : (
          <div className="max-h-96 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-white/[0.04] dark:border-white/[0.08]">
            {revisions.map((revision, index) => (
              <div
                key={revision.id || index}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                      v{revision.version}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
                        <span className="text-gray-700 dark:text-zinc-300">
                          {formatDate(revision.createdAt)}
                        </span>
                      </div>
                      {revision.message && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
                          <FileText className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{revision.message}</span>
                        </div>
                      )}
                      {revision.createdByUserId && (
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400 dark:text-zinc-600">
                          <User className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{revision.createdByUserId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {index > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRollbackTarget(revision)}
                    className="ml-4 flex-shrink-0"
                  >
                    <RotateCcw className="mr-1 h-4 w-4" />
                    Rollback
                  </Button>
                )}
                {index === 0 && (
                  <span className="ml-4 flex-shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                    Current
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
